import { Settings as SettingsIcon } from 'lucide-react';
import { useState } from '../hooks/useApi';
import { SectionHeader } from '../components/ui/section-header';
import { StatBadge } from '../components/ui/stat-badge';
import {
  formatCurrency,
  formatDuration,
  shortenAddress,
} from '../lib/utils';

export default function Settings() {
  const { data: state, error } = useState();

  if (error || !state) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-negative">
        Couldn&apos;t reach the bot API.
      </div>
    );
  }

  const modeRows: { label: string; value: React.ReactNode; note?: string }[] = [
    {
      label: 'Mode',
      value: state.mode.features.length ? (
        <StatBadge
          label={`auto-${state.mode.features.join('+')}`}
          variant={state.mode.dryRun ? 'pending' : 'positive'}
          dot
        />
      ) : (
        <StatBadge label="Observer (read-only)" variant="neutral" dot />
      ),
    },
    { label: 'Dry run', value: state.mode.dryRun ? 'YES' : 'no' },
    { label: 'HL network', value: state.mode.hlNetwork },
    { label: 'HL coin', value: state.position.hlCoin },
    { label: 'Active NFT', value: state.position.activeNftId ?? '—' },
    { label: 'Pool', value: shortenAddress(state.position.poolAddress) },
    { label: 'Bot uptime', value: formatDuration(state.uptimeMs) },
    {
      label: 'Last tick',
      value:
        state.lastTickAgeMs !== null ? `${formatDuration(state.lastTickAgeMs)} ago` : '—',
    },
  ];

  const thresholdRows: { label: string; value: React.ReactNode; note?: string }[] = [
    { label: 'Drift threshold', value: `${state.thresholds.driftBps} bps` },
    {
      label: 'Min notional (effective)',
      value: formatCurrency(state.thresholds.effectiveMinNotionalUsd),
      note: `HL floor $${state.thresholds.hlMinNotionalUsd} + ${state.thresholds.hlMinNotionalBufferBps} bps buffer`,
    },
    { label: 'LP range', value: `±${state.thresholds.lpRangePct}%` },
    {
      label: 'Hedge cooldown',
      value: formatDuration(state.thresholds.hedgeCooldownSec * 1000),
    },
    {
      label: 'Redeploy cooldown',
      value: formatDuration(state.thresholds.redeployCooldownSec * 1000),
    },
    {
      label: 'Harvest cooldown',
      value: formatDuration(state.thresholds.harvestCooldownSec * 1000),
    },
  ];

  const storageRows: { label: string; value: React.ReactNode }[] = [
    { label: 'Snapshots stored', value: state.counts.snapshotCount.toLocaleString() },
    { label: 'Actions stored', value: state.counts.actionCount.toLocaleString() },
    {
      label: 'Failures (24h)',
      value:
        state.counts.actionFailures24h > 0 ? (
          <span className="text-negative">{state.counts.actionFailures24h}</span>
        ) : (
          state.counts.actionFailures24h
        ),
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl">
      <SectionHeader
        title="Settings"
        subtitle="Configuration view. Live controls land in Commit C with API-key auth."
        size="lg"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SettingsCard title="Mode &amp; identity" rows={modeRows} />
        <SettingsCard title="Thresholds &amp; cooldowns" rows={thresholdRows} />
        <SettingsCard title="Storage" rows={storageRows} />

        {/* Controls placeholder — Commit C will fill this in. */}
        <div className="rounded-lg border border-border bg-surface p-6">
          <SectionHeader title="Controls" size="sm" className="mb-4" />
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center">
              <SettingsIcon className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Manual actions + auto-feature toggles arrive with Commit C.
            </p>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>Trigger a manual hedge with optional size cap</li>
            <li>Claim AERO + swap to USDC on demand</li>
            <li>Force-redeploy the LP at the current range pct</li>
            <li>Toggle AUTO_HEDGE / AUTO_REDEPLOY / AUTO_HARVEST live (no restart)</li>
            <li>Emergency pause (no auto actions for N minutes)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

interface SettingsCardProps {
  title: string;
  rows: { label: string; value: React.ReactNode; note?: string }[];
}

function SettingsCard({ title, rows }: SettingsCardProps) {
  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <SectionHeader title={title} size="sm" className="mb-4" />
      <dl className="divide-y divide-border">
        {rows.map(({ label, value, note }) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-4 py-2.5 first:pt-0 last:pb-0"
          >
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
            <dd className="text-sm font-medium tabular-nums text-right text-foreground">
              {value}
              {note && (
                <div className="text-[11px] text-muted-foreground font-normal mt-0.5">{note}</div>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
